import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, XCircle, ShieldAlert, Clock, Trash2, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface FailureAnalysisProps {
  campaignId: string;
}

interface FailureStats {
  type: string;
  count: number;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
  canRetry: boolean;
}

// Sanitize error messages - hide technical details from workspace users
const sanitizeErrorMessage = (reason: string | null): string => {
  if (!reason) return 'Unknown issue';
  
  const lowerReason = reason.toLowerCase();
  
  // Map technical errors to user-friendly messages
  if (lowerReason.includes('profile') && (lowerReason.includes('fetch') || lowerReason.includes('inaccessible')))
    return 'Could not access profile';
  if (lowerReason.includes('already_invited') || lowerReason.includes('already invited'))
    return 'Connection already sent';
  if (lowerReason.includes('rate') || lowerReason.includes('limit') || lowerReason.includes('throttl'))
    return 'Temporarily rate limited';
  if (lowerReason.includes('timeout'))
    return 'Connection not accepted in time';
  if (lowerReason.includes('not found') || lowerReason.includes('deleted') || lowerReason.includes('404'))
    return 'Profile no longer available';
  if (lowerReason.includes('private') || lowerReason.includes('restricted'))
    return 'Profile is private';
  if (lowerReason.includes('invalid') && lowerReason.includes('recipient'))
    return 'Could not reach profile';
  if (lowerReason.includes('sequence stopped') || lowerReason.includes('previous step'))
    return 'Sequence stopped';
  if (lowerReason.includes('campaign not active'))
    return 'Campaign paused';
  
  // Generic fallback - don't expose technical details
  return 'Processing issue';
};

export function FailureAnalysis({ campaignId }: FailureAnalysisProps) {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<string | null>(null);

  const { data: failedTargets, isLoading } = useQuery({
    queryKey: ['failed-targets', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('targets')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('status', 'failed');
      
      if (error) throw error;
      return data;
    }
  });

  // Calculate failure stats
  const failureStats: FailureStats[] = [
    {
      type: 'invalid_profile',
      count: failedTargets?.filter(t => t.failure_type === 'invalid_profile').length || 0,
      label: 'Invalid Profiles',
      icon: <XCircle className="w-5 h-5" />,
      description: 'Profile no longer available',
      color: 'text-red-600',
      canRetry: false
    },
    {
      type: 'access_denied',
      count: failedTargets?.filter(t => t.failure_type === 'access_denied').length || 0,
      label: 'Access Denied',
      icon: <ShieldAlert className="w-5 h-5" />,
      description: 'Profile is private',
      color: 'text-orange-600',
      canRetry: false
    },
    {
      type: 'connection_timeout',
      count: failedTargets?.filter(t => t.failure_type === 'connection_timeout').length || 0,
      label: 'Connection Timeouts',
      icon: <Clock className="w-5 h-5" />,
      description: 'No response to connection request',
      color: 'text-yellow-600',
      canRetry: false
    },
    {
      type: 'api_error',
      count: failedTargets?.filter(t => t.failure_type === 'api_error').length || 0,
      label: 'Temporary Issues',
      icon: <AlertCircle className="w-5 h-5" />,
      description: 'Temporary service issues - can be retried',
      color: 'text-blue-600',
      canRetry: true
    },
    {
      type: 'sequence_failure',
      count: failedTargets?.filter(t => t.failure_type === 'sequence_failure').length || 0,
      label: 'Sequence Failures',
      icon: <XCircle className="w-5 h-5" />,
      description: 'Failed at a step in automation sequence',
      color: 'text-purple-600',
      canRetry: false
    },
    {
      type: null,
      count: failedTargets?.filter(t => !t.failure_type).length || 0,
      label: 'Unknown Failures',
      icon: <AlertCircle className="w-5 h-5" />,
      description: 'No failure type specified',
      color: 'text-gray-600',
      canRetry: false
    }
  ];

  const totalFailed = failedTargets?.length || 0;

  // Mutation to delete targets by failure type
  const deleteByTypeMutation = useMutation({
    mutationFn: async (failureType: string | null) => {
      const query = supabase
        .from('targets')
        .delete()
        .eq('campaign_id', campaignId)
        .eq('status', 'failed');
      
      if (failureType === null) {
        query.is('failure_type', null);
      } else {
        query.eq('failure_type', failureType);
      }
      
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['failed-targets', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-targets', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-stats'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-progress'] });
      toast.success('Failed targets removed successfully');
      setDeleteDialogOpen(false);
      setDeleteType(null);
    },
    onError: (error: any) => {
      toast.error('Failed to remove targets: ' + error.message);
    }
  });

  // Mutation to retry API errors
  const retryApiErrorsMutation = useMutation({
    mutationFn: async () => {
      const apiErrorTargets = failedTargets?.filter(t => t.failure_type === 'api_error') || [];
      
      if (apiErrorTargets.length === 0) return;

      // Reset targets back to pending and clear failure info
      const { error: updateError } = await supabase
        .from('targets')
        .update({
          status: 'pending',
          failure_reason: null,
          failure_type: null,
          failed_at: null,
          current_sequence_step: 0
        })
        .eq('campaign_id', campaignId)
        .eq('status', 'failed')
        .eq('failure_type', 'api_error');
      
      if (updateError) throw updateError;

      // Delete any failed/cancelled jobs for these targets
      const targetIds = apiErrorTargets.map(t => t.id);
      const { error: jobDeleteError } = await supabase
        .from('job_queue')
        .delete()
        .in('target_id', targetIds)
        .in('status', ['failed', 'cancelled']);
      
      if (jobDeleteError) throw jobDeleteError;

      // Regenerate jobs for these targets
      const { data: campaign } = await supabase
        .from('campaigns')
        .select('*, sequences(*)')
        .eq('id', campaignId)
        .single();
      
      if (!campaign?.sequences?.[0]) throw new Error('No sequences found');
      
      const firstSequence = campaign.sequences[0];
      const jobs = apiErrorTargets.map((target, index) => {
        const delayMinutes = 5 + (index * 10);
        const scheduledFor = new Date(Date.now() + delayMinutes * 60 * 1000);

        return {
          client_id: campaign.client_id,
          campaign_id: campaignId,
          target_id: target.id,
          job_type: firstSequence.action_type === 'connect' ? 'send_connection' : 
                    firstSequence.action_type === 'visit' ? 'visit_profile' : 'send_message',
          action_type_category: firstSequence.action_type === 'connect' ? 'connection' : 
                               firstSequence.action_type,
          payload: { profileUrl: target.profile_url },
          scheduled_for: scheduledFor.toISOString(),
          priority: 5,
          status: 'pending',
          max_retries: 0,
          retry_count: 0
        };
      });

      const { error: jobInsertError } = await supabase
        .from('job_queue')
        .insert(jobs);
      
      if (jobInsertError) throw jobInsertError;

      return apiErrorTargets.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['failed-targets', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-targets', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-stats'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-progress'] });
      toast.success(`${count} targets queued for retry`);
    },
    onError: (error: any) => {
      toast.error('Failed to retry targets: ' + error.message);
    }
  });

  const handleDeleteType = (type: string | null) => {
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">Loading failure analysis...</p>
      </Card>
    );
  }

  if (totalFailed === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-green-600">
          <CheckCircle2 className="w-6 h-6" />
          <div>
            <h3 className="font-semibold">No Failures</h3>
            <p className="text-sm text-muted-foreground">All targets are processing successfully</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Failure Analysis
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {totalFailed} total failed targets across {failureStats.filter(s => s.count > 0).length} failure types
            </p>
          </div>
          <Badge variant="destructive" className="text-lg px-4 py-1">
            {totalFailed} Failed
          </Badge>
        </div>

        {/* Failure Type Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {failureStats
            .filter(stat => stat.count > 0)
            .map((stat) => (
              <div 
                key={stat.type || 'unknown'} 
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`${stat.color}`}>
                    {stat.icon}
                  </div>
                  <Badge variant="outline" className="text-lg font-bold">
                    {stat.count}
                  </Badge>
                </div>
                <h4 className="font-semibold mb-1">{stat.label}</h4>
                <p className="text-xs text-muted-foreground mb-3">{stat.description}</p>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteType(stat.type)}
                    disabled={deleteByTypeMutation.isPending}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Remove
                  </Button>
                  {stat.canRetry && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => retryApiErrorsMutation.mutate()}
                      disabled={retryApiErrorsMutation.isPending}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            ))}
        </div>

        {/* Bulk Actions */}
        <div className="border-t pt-4">
          <h4 className="font-semibold mb-3 text-sm">Bulk Actions</h4>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteType('invalid_profile')}
              disabled={
                deleteByTypeMutation.isPending || 
                (failedTargets?.filter(t => t.failure_type === 'invalid_profile').length || 0) === 0
              }
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Remove All Invalid Profiles
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteType('access_denied')}
              disabled={
                deleteByTypeMutation.isPending || 
                (failedTargets?.filter(t => t.failure_type === 'access_denied').length || 0) === 0
              }
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Remove All Access Denied
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => retryApiErrorsMutation.mutate()}
              disabled={
                retryApiErrorsMutation.isPending || 
                (failedTargets?.filter(t => t.failure_type === 'api_error').length || 0) === 0
              }
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry All Temporary Issues
            </Button>
          </div>
        </div>

        {/* Recent Failures List */}
        <div className="border-t mt-4 pt-4">
          <h4 className="font-semibold mb-3 text-sm">Recent Failures</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {failedTargets?.slice(0, 10).map((target) => (
              <div 
                key={target.id} 
                className="flex items-start justify-between p-3 bg-muted/30 rounded text-sm"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {target.first_name} {target.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {target.company || 'No company'} • {target.title || 'No title'}
                  </p>
                  <p className="text-xs text-destructive mt-1 truncate">
                    {sanitizeErrorMessage(target.failure_reason)}
                  </p>
                </div>
                <Badge variant="outline" className="ml-2 text-xs">
                  {target.failure_type?.replace(/_/g, ' ') || 'unknown'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Failed Targets</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently remove all targets with failure type:{" "}
              <strong className="text-destructive">
                {deleteType ? deleteType.replace(/_/g, ' ') : 'unknown'}
              </strong>
              ?
              <br />
              <br />
              This will delete:
              <ul className="list-disc list-inside mt-2">
                <li>
                  {failedTargets?.filter(t => 
                    deleteType === null ? !t.failure_type : t.failure_type === deleteType
                  ).length || 0} targets
                </li>
                <li>All associated action logs</li>
                <li>All pending actions for these targets</li>
              </ul>
              <br />
              <strong className="text-destructive">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteType !== undefined && deleteByTypeMutation.mutate(deleteType)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Targets
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}